import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@heroui/react'
import { Pencil, Trash2 } from 'lucide-react'
import ChipButton from '@/shared/components/ui/Button/ChipButton'
import InlineEditChip from '@/shared/components/ui/Button/InlineEditChip'
import { PrimaryButton } from '@/shared/components/ui/Button/PrimaryButton'
import TextButton from '@/shared/components/ui/Button/TextButton'
import CommonInput from '@/shared/components/ui/Input/CommonInput'
import QueryTextInput from '@/shared/components/ui/Input/QueryTextInput'
import {
    PART_LABELS,
    SUGGESTIONS,
    removePart,
    replacePart,
    segmentQuery,
    type PartKind,
    type QueryCriteria,
    type QueryPart,
} from './queryParts'

const ChipButtonComponent = ChipButton as React.ComponentType<any>
const TextButtonComponent = TextButton as React.ComponentType<any>
const CommonInputComponent = CommonInput as React.ComponentType<any>
const QueryTextInputComponent = QueryTextInput as React.ComponentType<any>
const PrimaryButtonComponent = PrimaryButton as React.ComponentType<any>
const InlineEditChipComponent = InlineEditChip as React.ComponentType<any>

interface EditableQueryProps {
    /** The query as typed. */
    value: string
    /** Keeps the parent's draft in sync while the user types. */
    onChange?: (text: string) => void
    /** Runs a new search with the full query text. */
    onSubmit?: (text: string) => void
    /** Criteria the server extracted for the saved query; sharpens part detection. */
    criteria?: QueryCriteria | null
    /** Extra suggestions per kind, e.g. industries from the user's saved searches. */
    extraSuggestions?: Partial<Record<PartKind, string[]>>
    placeholder?: string
}

const unique = (items: string[]) => {
    const seen = new Set<string>()
    return items.filter((item) => {
        const key = item.trim().toLowerCase()
        if (!key || seen.has(key)) return false
        seen.add(key)
        return true
    })
}

interface PartEditorProps {
    part: QueryPart
    suggestions: string[]
    onApply: (value: string) => void
    onRemove: () => void
    onClose: () => void
}

const PartEditor = ({ part, suggestions, onApply, onRemove, onClose }: PartEditorProps) => {
    const [draft, setDraft] = useState(part.text)
    const inputRef = useRef<HTMLInputElement | null>(null)

    useEffect(() => {
        inputRef.current?.focus()
        inputRef.current?.select()
    }, [])

    const apply = (value: string) => {
        const next = value.trim()
        if (!next) return onRemove()
        if (next === part.text) return onClose()
        onApply(next)
    }

    return (
        <div className='flex w-[340px] max-w-[88vw] flex-col gap-3 p-3'>
            <div className='flex items-center justify-between'>
                <span className='text-xs font-medium text-gray-500 dark:text-gray-400'>
                    {PART_LABELS[part.kind]}
                </span>
                <TextButtonComponent onClick={onRemove}>
                    <span className='flex items-center gap-1'>
                        <Trash2 size={12} /> Remove
                    </span>
                </TextButtonComponent>
            </div>

            <div className='flex items-center gap-2'>
                <CommonInputComponent
                    ref={inputRef}
                    value={draft}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => setDraft(event.target.value)}
                    onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                        if (event.key === 'Enter') {
                            event.preventDefault()
                            apply(draft)
                        }
                        if (event.key === 'Escape') onClose()
                    }}
                    wrapperClassName='flex-1 min-w-0'
                    aria-label={`Change ${PART_LABELS[part.kind].toLowerCase()}`}
                />
                <PrimaryButtonComponent startContent={false} onClick={() => apply(draft)} className='shrink-0'>
                    Apply
                </PrimaryButtonComponent>
            </div>

            {suggestions.length > 0 && (
                <div className='flex flex-col gap-1.5'>
                    <span className='text-[0.7rem] font-medium text-gray-500 dark:text-gray-400'>Suggestions</span>
                    <div className='flex flex-wrap gap-1.5'>
                        {suggestions.map((suggestion) => (
                            <ChipButtonComponent key={suggestion} onClick={() => apply(suggestion)}>
                                {suggestion}
                            </ChipButtonComponent>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}

export default function EditableQuery({
    value,
    onChange,
    onSubmit,
    criteria,
    extraSuggestions,
    placeholder = 'Describe the companies you want to find...',
}: EditableQueryProps) {
    const [mode, setMode] = useState<'parts' | 'text'>(value.trim() ? 'parts' : 'text')
    const [openStart, setOpenStart] = useState<number | null>(null)
    const segments = useMemo(() => segmentQuery(value, criteria), [value, criteria])
    const hasParts = segments.some((segment) => segment.type === 'part')

    // A new search (empty query) goes straight to typing.
    useEffect(() => {
        if (!value.trim()) setMode('text')
    }, [value])

    const search = (text: string) => {
        const next = text.trim()
        if (!next) return
        onChange?.(next)
        onSubmit?.(next)
    }

    if (mode === 'text' || !hasParts) {
        return (
            <div className='flex min-w-0 flex-1 items-center gap-2'>
                {/* Grows to show the whole query while editing; Enter searches. */}
                <QueryTextInputComponent
                    value={value}
                    onChange={(text: string) => onChange?.(text)}
                    onSubmit={search}
                    onBlur={() => value.trim() && setMode('parts')}
                    // Opened from Edit: focus it so a click outside blurs back to view mode.
                    autoFocus={mode === 'text' && Boolean(value.trim())}
                    placeholder={placeholder}
                    ariaLabel='Search query'
                    textClassName='text-gray-500 dark:text-gray-200'
                />
            </div>
        )
    }

    return (
        <div className='group flex min-w-0 flex-1 items-center gap-2'>
            <div className='min-w-0 flex-1 truncate py-1 text-sm leading-7 text-gray-500 dark:text-gray-400'>
                {segments.map((segment, index) => {
                    if (segment.type === 'text') {
                        return <span key={`t-${index}`} className='text-gray-500'>{segment.text}</span>
                    }
                    const { part } = segment
                    const suggestions = unique([
                        ...(extraSuggestions?.[part.kind] ?? []),
                        ...SUGGESTIONS[part.kind],
                    ])
                        .filter((item) => item.trim().toLowerCase() !== part.text.trim().toLowerCase())
                        .slice(0, 8)
                    return (
                        <Popover
                            key={`p-${part.start}-${part.kind}`}
                            isOpen={openStart === part.start}
                            onOpenChange={(open) => setOpenStart(open ? part.start : null)}
                            placement='bottom-start'
                            offset={8}
                        >
                            <PopoverTrigger>
                                <InlineEditChipComponent
                                    ariaLabel={`Change ${PART_LABELS[part.kind].toLowerCase()}: ${part.text}`}
                                >
                                    {part.text}
                                </InlineEditChipComponent>
                            </PopoverTrigger>
                            <PopoverContent>
                                <PartEditor
                                    key={`${part.start}-${part.text}`}
                                    part={part}
                                    suggestions={suggestions}
                                    onClose={() => setOpenStart(null)}
                                    onApply={(next) => {
                                        setOpenStart(null)
                                        search(replacePart(value, part, next))
                                    }}
                                    onRemove={() => {
                                        setOpenStart(null)
                                        search(removePart(value, part))
                                    }}
                                />
                            </PopoverContent>
                        </Popover>
                    )
                })}
            </div>
            <div className='opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto focus-within:opacity-100 focus-within:pointer-events-auto transition-opacity duration-150 shrink-0'>
                <TextButtonComponent onClick={() => setMode('text')} tooltip='Edit the whole query as text' className="text-gray-600 hover:bg-inherit ">
                    <span className='flex items-center gap-1'>
                        <Pencil size={12} /> Edit
                    </span>
                </TextButtonComponent>
            </div>
        </div>
    )
}
